import { skipToken } from '@reduxjs/toolkit/query/react'
import { useEffect, useMemo, useRef } from 'react'

import {
  resourceTag,
  useOpenWindow,
  useWindow,
  useDebouncedValue,
  useQueryResult,
} from '@krp/client-runtime'
import {
  AllocationSection,
  EMPTY_FORMS,
  EMPTY_LINE_FILTER,
  HistorySection,
  LinesSection,
  ONEC_SUPPLY_TAG,
  ONEC_SUPPLY_WINDOWS,
  PostingSection,
  PricesSection,
  SupplyScreen,
  addLineChanged,
  allocationDraftDiscarded,
  allocationDraftRebased,
  allocationDraftRetried,
  allocationEdited,
  buildAddLine,
  buildAllToOne,
  buildDefaultWarehouse,
  buildHeaderUpdate,
  buildInvoiceFill,
  buildOverrideMatch,
  buildPost,
  buildPriceFactor,
  buildPrices,
  commandDismissed,
  formChanged,
  headerDiscarded,
  headerEdited,
  headerRebased,
  historyMoreRequested,
  lineDraftDiscarded,
  lineDraftRebased,
  lineDraftRetried,
  lineEdited,
  lineFilterChanged,
  makeSelectAllocationVm,
  makeSelectHistoryVm,
  makeSelectLinesVm,
  makeSelectPostingVm,
  makeSelectPricesVm,
  makeSelectSupplyVm,
  normalizeRid,
  onecSupply,
  onecSupplyAllocations,
  onecSupplyHistory,
  onecSupplyLines,
  onecSupplyPostingLog,
  onecSupplyPostingStatus,
  onecSupplyPreview,
  onecSupplyPriceTypes,
  onecSupplyValidate,
  onecStructure,
  overrideTyped,
  postingStatusCheckRequested,
  priceTyped,
  pricesCleared,
  reloadStructure,
  sectionChosen,
  supplyCommandRequested,
  supplyCommandResendRequested,
  supplyKey,
  selectStructureWarehouses,
  suppliesApi,
  type AllocationActions,
  type HistoryActions,
  type LinesActions,
  type PostingActions,
  type PricesActions,
  type SupplyActions,
  type SupplyArgs,
  type SupplyCommandIntent,
  type SupplyLinesArgs,
  type SupplyPagedArgs,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { localToday } from './supplyDates'

/** Пока сага проведения идёт, статус перечитывается (только GET); после — опрос снят. */
const POSTING_POLL_MS = 1500
/** Версия нового клиента в отчёте проведения: номер релиза выдаёт выкладка через /health служб (§0). */
const CLIENT_LABEL = 'krp web (resource-onec-ui, UB1c)'

/**
 * Binding окна поставки. rid и id — ИЗ АДРЕСА окна, не из «выбранной базы»:
 * окно, открытое на базе А, остаётся на базе А, что бы ни выбрали в шапке.
 * Запросы раздела подписываются только когда раздел открыт.
 */
export function SupplyBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = normalizeRid(window.params.rid ?? '')
  const supplyId = (window.params.supplyId ?? '').toLowerCase()
  const key = supplyKey(resourceId, supplyId)
  const intent = useAppSelector((state) => state.onec.supplies)
  const section = intent.section[key] ?? 'lines'
  const lineFilter = intent.lineFilter[key] ?? EMPTY_LINE_FILTER
  const pages = intent.historyPages[key] ?? 1
  const search = useDebouncedValue(lineFilter.search)

  const valid = resourceId !== '' && supplyId !== ''
  const supplyArgs = useMemo<SupplyArgs | null>(() => (valid ? { resourceId, supplyId } : null), [valid, resourceId, supplyId])
  const resourceArgs = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const needLines = section === 'lines' || section === 'prices'
  const linesArgs = useMemo<SupplyLinesArgs | null>(
    () => (valid && needLines ? { resourceId, supplyId, search: section === 'lines' ? search : '', onlyProblems: section === 'lines' && lineFilter.onlyProblems } : null),
    [valid, needLines, resourceId, supplyId, search, section, lineFilter.onlyProblems],
  )
  const historyArgs = useMemo<SupplyPagedArgs | null>(
    () => (valid && section === 'history' ? { resourceId, supplyId, pages } : null),
    [valid, section, resourceId, supplyId, pages],
  )
  const when = (wanted: boolean) => (wanted ? supplyArgs : null)

  const supply = useQueryResult(onecSupply, supplyArgs)
  const warehouses = useQueryResult(onecStructure, resourceArgs)
  const lines = useQueryResult(onecSupplyLines, linesArgs)
  const matrix = useQueryResult(onecSupplyAllocations, when(section === 'allocation'))
  const problems = useQueryResult(onecSupplyValidate, when(section === 'allocation'))
  const priceTypes = useQueryResult(onecSupplyPriceTypes, when(section === 'prices'))
  const preview = useQueryResult(onecSupplyPreview, when(section === 'posting'))
  const history = useQueryResult(onecSupplyHistory, historyArgs)
  const postingLog = useQueryResult(onecSupplyPostingLog, when(section === 'history'))

  const selectors = useMemo(
    () => ({
      supply: makeSelectSupplyVm(),
      lines: makeSelectLinesVm(),
      allocation: makeSelectAllocationVm(),
      prices: makeSelectPricesVm(),
      posting: makeSelectPostingVm(),
      history: makeSelectHistoryVm(),
    }),
    [],
  )
  const vm = selectors.supply({ resourceId, supplyId, supply, warehouses, intent })
  const postingStatusArgs = section === 'posting' && supplyArgs !== null ? supplyArgs : skipToken
  const statusSelect = useMemo(() => onecSupplyPostingStatus.select(postingStatusArgs), [postingStatusArgs])
  const status = useAppSelector(statusSelect)
  const postingVm = selectors.posting({ resourceId, supplyId, supply, preview, status, intent, clientVersion: CLIENT_LABEL })
  onecSupplyPostingStatus.useQuerySubscription(postingStatusArgs, { pollingInterval: postingVm.poll ? POSTING_POLL_MS : 0 })
  // Сага закончилась, а поставка в кэше ещё «Проводится»: перечитать поставку один раз.
  const outdated = postingVm.supplyOutdated
  useEffect(() => {
    if (outdated) dispatch(suppliesApi.util.invalidateTags([resourceTag(ONEC_SUPPLY_TAG, resourceId, supplyId)]))
  }, [dispatch, outdated, resourceId, supplyId])

  const linesVm = selectors.lines({ resourceId, supplyId, lines, editable: vm.editable, intent })
  const allocationVm = selectors.allocation({ resourceId, supplyId, matrix, problems, warehouses, editable: vm.editable, intent })
  const pricesVm = selectors.prices({ resourceId, supplyId, lines, priceTypes, editable: vm.editable, intent })
  const historyVm = selectors.history({ history, log: postingLog })

  // Последние данные для обработчиков: actions не пересоздаются на каждый ответ службы.
  const latest = useRef({ vm, supply, warehouses, lines, matrix, priceTypes, preview, intent, postingVm })
  useEffect(() => {
    latest.current = { vm, supply, warehouses, lines, matrix, priceTypes, preview, intent, postingVm }
  }, [vm, supply, warehouses, lines, matrix, priceTypes, preview, intent, postingVm])

  const handlers = useMemo(() => {
    const ref = { resourceId, supplyId }
    const version = () => latest.current.supply.data?.configurationVersion ?? ''
    const supplyData = () => latest.current.supply.data?.data
    const lineOf = (lineId: string) => latest.current.lines.data?.items.find((line) => line.id === lineId)
    const rowOf = (lineId: string) => latest.current.matrix.data?.data.rows.find((row) => row.lineId === lineId)
    const forms = () => latest.current.intent.forms[key] ?? EMPTY_FORMS
    const command = (intent: Omit<SupplyCommandIntent, 'resourceId' | 'supplyId' | 'expectedConfigurationVersion'>) =>
      dispatch(supplyCommandRequested({ ...ref, ...intent, expectedConfigurationVersion: version() }))
    const reloadSupply = () => dispatch(suppliesApi.util.invalidateTags([resourceTag(ONEC_SUPPLY_TAG, resourceId, supplyId)]))
    // «Повторить» после отказа 1С у выбора склада: склады — структура базы, служба читает её из 1С на лету (RP-LSB2).
    const reloadWarehouses = () => dispatch(reloadStructure(resourceId))

    const supplyActions: SupplyActions = {
      sectionChosen: (value) => dispatch(sectionChosen({ ...ref, section: value })),
      headerChanged: (field, value) => {
        const server = latest.current.vm.header?.fields[field].server
        if (server !== undefined) dispatch(headerEdited({ ...ref, field, value, server }))
      },
      saveHeader: () => {
        const current = supplyData()
        const draft = latest.current.intent.headerDrafts[key]
        if (current === undefined || draft === undefined) return
        const built = buildHeaderUpdate(resourceId, draft, current, (latest.current.warehouses.data === undefined ? [] : selectStructureWarehouses(latest.current.warehouses.data.data)))
        if (built.ok) command({ kind: 'update', data: built.data })
      },
      discardHeader: () => dispatch(headerDiscarded(ref)),
      keepMineHeader: () => {
        const fields = latest.current.vm.header?.fields
        if (fields === undefined) return
        dispatch(
          headerRebased({
            ...ref,
            server: {
              documentDate: fields.documentDate.server,
              supplierInvoiceNumber: fields.supplierInvoiceNumber.server,
              comment: fields.comment.server,
              warehouseKey: fields.warehouseKey.server,
            },
          }),
        )
      },
      invoiceChanged: (patch) =>
        dispatch(
          formChanged({
            ...ref,
            patch: {
              ...(patch.fileId !== undefined ? { invoiceFileId: patch.fileId } : {}),
              ...(patch.mode !== undefined ? { invoiceMode: patch.mode } : {}),
            },
          }),
        ),
      fillFromInvoice: () => {
        const built = buildInvoiceFill(forms().invoiceFileId, forms().invoiceMode)
        if (built.ok) command({ kind: 'import-invoice', data: built.data })
      },
      clone: () => command({ kind: 'clone', data: null, query: { documentDate: localToday() } }),
      remove: () => {
        if (globalThis.confirm('Удалить черновик поставки? История останется на сервере.')) command({ kind: 'delete', data: null })
      },
      openClone: () => {
        const cloned = latest.current.vm.cloned
        if (cloned !== null) openWindow(ONEC_SUPPLY_WINDOWS.supply, { rid: resourceId, supplyId: cloned.id })
      },
      resend: (commandKey) => dispatch(supplyCommandResendRequested({ key: commandKey })),
      dismiss: (commandKey) => dispatch(commandDismissed({ key: commandKey })),
      reload: reloadSupply,
      reloadWarehouses,
      close: () => {
        // Закрытие окна внутри приложения — то же предупреждение, что и уход со страницы.
        if (latest.current.vm.unsaved && !globalThis.confirm('Есть несохранённые правки или команда без итога. Закрыть окно?')) return
        window.close()
      },
    }

    const linesActions: LinesActions = {
      filterChanged: (patch) => dispatch(lineFilterChanged({ ...ref, patch })),
      edit: (lineId, field, value) => {
        const line = lineOf(lineId)
        if (line !== undefined) dispatch(lineEdited({ ...ref, lineId, field, value, version: line.version, configurationVersion: version() }))
      },
      keepMine: (lineId, policy) => {
        const line = lineOf(lineId)
        if (line !== undefined) {
          dispatch(
            lineDraftRebased({
              ...ref,
              lineId,
              version: line.version,
              configurationVersion: version(),
              ...(policy !== undefined ? { allocationPolicy: policy } : {}),
            }),
          )
        }
      },
      takeServer: (lineId) => dispatch(lineDraftDiscarded({ ...ref, lineId })),
      retry: (lineId) => dispatch(lineDraftRetried({ ...ref, lineId })),
      toggleIgnored: (lineId) => {
        const line = lineOf(lineId)
        if (line !== undefined) command({ kind: 'set-ignored', lineId, detail: lineId, data: { version: line.version, ignored: !line.isIgnored } })
      },
      remove: (lineId) => command({ kind: 'delete-line', lineId, detail: lineId, data: null }),
      match: (force) => command({ kind: 'match', detail: force ? 'force' : 'auto', data: null, query: { force: force ? 'true' : 'false' } }),
      resetMatch: (lineId) => command({ kind: 'reset-match', lineId, detail: lineId, data: null }),
      markNew: (lineId) => command({ kind: 'mark-new', lineId, detail: lineId, data: {} }),
      overrideTyped: (lineId, value) => dispatch(overrideTyped({ ...ref, lineId, value })),
      override: (lineId, productId) => {
        const built = buildOverrideMatch(productId ?? forms().overrideProduct[lineId.toLowerCase()] ?? '')
        if (built.ok) command({ kind: 'override-match', lineId, detail: lineId, data: built.data })
      },
      addLineChanged: (patch) => dispatch(addLineChanged({ ...ref, patch })),
      addLine: () => {
        const built = buildAddLine(forms().addLine)
        if (!built.ok) return
        command({ kind: 'add-line', data: built.data })
        dispatch(addLineChanged({ ...ref, patch: { name: '', article: '', barcode: '', quantity: '', purchasePrice: '' } }))
      },
      factorChanged: (value) => dispatch(formChanged({ ...ref, patch: { priceFactor: value } })),
      previewFactor: () => {
        const built = buildPriceFactor(forms().priceFactor, true)
        if (built.ok) command({ kind: 'price-factor', detail: 'preview', data: built.data })
      },
      applyFactor: () => {
        const built = buildPriceFactor(forms().priceFactor, false)
        if (built.ok) command({ kind: 'price-factor', detail: 'apply', data: built.data })
      },
      reload: reloadSupply,
    }

    const allocationActions: AllocationActions = {
      edit: (lineId, warehouseKey, value) => {
        const row = rowOf(lineId)
        if (row !== undefined) {
          dispatch(allocationEdited({ ...ref, lineId, warehouseKey, value, version: row.version, base: row.cells, configurationVersion: version() }))
        }
      },
      keepMine: (lineId) => {
        const row = rowOf(lineId)
        if (row !== undefined) dispatch(allocationDraftRebased({ ...ref, lineId, version: row.version, base: row.cells, configurationVersion: version() }))
      },
      takeServer: (lineId) => dispatch(allocationDraftDiscarded({ ...ref, lineId })),
      retry: (lineId) => dispatch(allocationDraftRetried({ ...ref, lineId })),
      fillWarehouseChanged: (warehouseKey) => dispatch(formChanged({ ...ref, patch: { fillWarehouseKey: warehouseKey } })),
      // Массовое заполнение ПРИМЕНЯЕТСЯ (B-1): ручное распределение строк заменяется — сначала согласие человека.
      allToOne: () => {
        const built = buildAllToOne(resourceId, forms().fillWarehouseKey)
        if (built.ok && globalThis.confirm('Распределить все строки на выбранный склад? Набранное вручную распределение заменится.')) {
          command({ kind: 'fill-all-to-one', data: built.data })
        }
      },
      evenly: () => {
        if (globalThis.confirm('Разложить все строки поровну по складам? Набранное вручную распределение заменится.')) command({ kind: 'fill-evenly', data: {} })
      },
      clear: () => {
        if (globalThis.confirm('Очистить распределение всех строк поставки?')) command({ kind: 'fill-clear', data: {} })
      },
      setDefaultWarehouse: () => {
        const current = supplyData()
        if (current === undefined) return
        const built = buildDefaultWarehouse(resourceId, forms().fillWarehouseKey, current)
        if (built.ok) command({ kind: 'default-warehouse', data: built.data })
      },
      reloadWarehouses,
      reload: reloadSupply,
    }

    const pricesActions: PricesActions = {
      typed: (priceKey, value) => dispatch(priceTyped({ ...ref, key: priceKey, value })),
      save: () => {
        const built = buildPrices(resourceId, forms().prices, latest.current.lines.data?.items ?? [], latest.current.priceTypes.data?.data.items ?? [])
        if (built.ok) command({ kind: 'set-prices', data: built.data })
      },
      discard: () => dispatch(pricesCleared(ref)),
      reload: reloadSupply,
    }

    const postingActions: PostingActions = {
      post: () => {
        const current = supplyData()
        const shown = latest.current.preview.data?.data
        if (current === undefined || shown === undefined) return
        const built = buildPost(shown, current)
        // Номер последней попытки ДО нажатия: «Проверить статус» отличит новую попытку от старой.
        if (built.ok) command({ kind: 'post', data: built.data, baseline: current.lastPostingAttempt })
      },
      checkStatus: () => dispatch(postingStatusCheckRequested(ref)),
      reloadPreview: reloadSupply,
      copyReport: () => {
        void globalThis.navigator.clipboard?.writeText(latest.current.postingVm.report)
      },
      dismiss: (commandKey) => dispatch(commandDismissed({ key: commandKey })),
    }

    const historyActions: HistoryActions = {
      loadMore: () => dispatch(historyMoreRequested({ ...ref, pages: (latest.current.intent.historyPages[key] ?? 1) + 1 })),
      reload: reloadSupply,
    }

    return { supplyActions, linesActions, allocationActions, pricesActions, postingActions, historyActions }
  }, [dispatch, key, openWindow, resourceId, supplyId, window])

  return (
    <SupplyScreen vm={vm} actions={handlers.supplyActions}>
      {section === 'lines' && <LinesSection vm={linesVm} actions={handlers.linesActions} />}
      {section === 'allocation' && <AllocationSection vm={allocationVm} actions={handlers.allocationActions} />}
      {section === 'prices' && <PricesSection vm={pricesVm} actions={handlers.pricesActions} />}
      {section === 'posting' && <PostingSection vm={postingVm} actions={handlers.postingActions} />}
      {section === 'history' && <HistorySection vm={historyVm} actions={handlers.historyActions} />}
    </SupplyScreen>
  )
}
